import { useState } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useAuth } from "../App";
import { toast } from "sonner";
import { User, Briefcase, Award, ArrowRight } from "lucide-react";
import { LogoIcon } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: "ops", label: "Operations", description: "Operations Manager / Director" },
  { id: "quality", label: "Quality", description: "Quality Engineer / Manager" },
  { id: "pm", label: "Project Management", description: "PM / Program Manager" },
  { id: "consultant", label: "Consultant", description: "Improvement Consultant" },
];

const EXPERIENCE_LEVELS = [
  { id: "white_belt", label: "White Belt", description: "New to Lean Six Sigma" },
  { id: "green_belt", label: "Green Belt", description: "Experienced with DMAIC basics" },
  { id: "black_belt", label: "Black Belt", description: "Advanced statistical analysis" },
];

const INDUSTRIES = [
  "Healthcare", "Manufacturing", "Finance", "Technology", "Retail",
  "Logistics", "Pharmaceuticals", "Aerospace", "Automotive", "Other"
];

export default function ProfileSetup({ onComplete }) {
  const { token, login, user } = useAuth();
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    role: "",
    industry: "",
    experience_level: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!profile.role || !profile.experience_level) {
      toast.error("Please select your role and experience level");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profile),
      });

      if (!response.ok) throw new Error("Failed to save profile");

      const updatedUser = await response.json();
      login(token, updatedUser);
      toast.success("Profile setup complete!");
      onComplete?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <LogoIcon className="w-14 h-14" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Welcome to BlackBelt AI</h1>
          <p className="text-muted-foreground">
            Let's customize your experience based on your background
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full transition-colors ${
                step >= s ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>

        <div className="bg-card border border-border rounded-lg p-8">
          {/* Step 1: Role */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Briefcase className="w-5 h-5 text-primary" />
                <h2 className="font-heading font-semibold">What's your role?</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ROLES.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setProfile({ ...profile, role: role.id })}
                    className={`p-4 text-left border rounded-lg transition-all ${
                      profile.role === role.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid={`role-${role.id}`}
                  >
                    <p className="font-medium">{role.label}</p>
                    <p className="text-sm text-muted-foreground">{role.description}</p>
                  </button>
                ))}
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!profile.role}
                className="w-full gap-2"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Step 2: Experience Level */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Award className="w-5 h-5 text-primary" />
                <h2 className="font-heading font-semibold">What's your Lean Six Sigma experience?</h2>
              </div>

              <div className="space-y-4">
                {EXPERIENCE_LEVELS.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => setProfile({ ...profile, experience_level: level.id })}
                    className={`w-full p-4 text-left border rounded-lg transition-all ${
                      profile.experience_level === level.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid={`experience-${level.id}`}
                  >
                    <p className="font-medium">{level.label}</p>
                    <p className="text-sm text-muted-foreground">{level.description}</p>
                  </button>
                ))}
              </div>

              <p className="text-sm text-muted-foreground">
                This helps us adjust the level of guidance and explanations throughout the app.
              </p>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!profile.experience_level}
                  className="flex-1 gap-2"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Industry */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-5 h-5 text-primary" />
                <h2 className="font-heading font-semibold">What industry do you work in?</h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {INDUSTRIES.map((industry) => (
                  <button
                    key={industry}
                    onClick={() => setProfile({ ...profile, industry })}
                    className={`p-3 text-center border rounded-lg transition-all text-sm ${
                      profile.industry === industry
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid={`industry-${industry.toLowerCase()}`}
                  >
                    {industry}
                  </button>
                ))}
              </div>

              <p className="text-sm text-muted-foreground">
                Optional - helps provide industry-relevant examples and templates.
              </p>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                  Back
                </Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1 gap-2">
                  {saving ? "Saving..." : "Complete Setup"}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          You can update these settings anytime from your profile.
        </p>
      </div>
    </div>
  );
}
